import { Fragment, type ReactElement } from 'react';
import { Box, Group, Loader, Stack, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import { accentRgba, accentTokens, type VizAccentTokens } from '@/design/VizAccents';
import { formatCount, formatPercent } from '@/shared/analytics/MetricFormatting';
import { formatSignalValue } from './SignalFormatting';
import type { SignalData, SignalDelta, SignalTileDef } from './SignalTypes';
import { SEVERITY_COLORS } from './SignalSeverityColors';
import Sparkline from '@/design/Sparkline/Sparkline';
import SignalBars from './SignalBars';

interface SignalTileProps {
    def: SignalTileDef;
    /** The live data once resolved, or the deterministic sample fallback — held behind the loader while `loading`. */
    data: SignalData;
    /** Whether the data shown was produced by the live loader (vs the deterministic sample). */
    isLive: boolean;
    /** Whether the live loader is still in flight. */
    loading: boolean;
}


/** Kinds whose body anchors to the bottom of the card so headline numbers align across a row. */
const BOTTOM_ANCHORED = new Set(['kpi', 'gauge']);

export default function SignalTile({ def, data, isLive, loading }: SignalTileProps): ReactElement {
    const tokens = accentTokens(def.accent);
    const Icon = def.icon;
    const anchor = BOTTOM_ANCHORED.has(def.kind) ? 'flex-end' : 'flex-start';

    const card = (
        <SurfaceCard $padding="md" style={{ height: '100%', borderTop: `2px solid ${tokens.solid}`, position: 'relative' }}>
            <Group gap="sm" wrap="nowrap" align="flex-start" mb={6}>
                <Box
                    style={{
                        width: 30,
                        height: 30,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: tokens.soft,
                        border: `1px solid ${tokens.border}`,
                        borderRadius: 'var(--bm-radius)',
                    }}
                >
                    <Icon size={15} color={tokens.solid} />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={6} wrap="nowrap" align="center" justify="space-between">
                        <Text fw={700} truncate style={{ fontSize: '14px', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                            {def.title}
                        </Text>
                        {def.to !== undefined && <ArrowUpRight size={14} color="var(--bm-color-text-dimmed)" style={{ flexShrink: 0 }} />}
                    </Group>
                </Box>
            </Group>

            <Text size="xs" c="dimmed" lh={1.45} mb="sm" lineClamp={3} style={{ fontSize: '11px' }}>
                {def.description}
            </Text>

            <Box style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: anchor, minHeight: 0 }}>
                <SignalBody def={def} data={data} tokens={tokens} />
                {loading && (
                    <Box
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'var(--bm-color-body)',
                            borderRadius: 'var(--bm-radius)',
                        }}
                    >
                        <Loader size="sm" color={tokens.solid} />
                    </Box>
                )}
            </Box>

            <SourceTag isLive={isLive} loading={loading} accent={tokens} />
        </SurfaceCard>
    );

    if (def.to !== undefined) {
        return (
            <Link to={def.to} style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}>
                {card}
            </Link>
        );
    }
    return card;
}

function SignalBody({ def, data, tokens }: { def: SignalTileDef; data: SignalData; tokens: VizAccentTokens }): ReactElement | null {
    switch (def.kind) {
        case 'kpi':
            return <KpiBody data={data} tokens={tokens} />;
        case 'bars':
            return <SignalBars data={data} tokens={tokens} />;
        case 'donut':
            return <DonutBody data={data} tokens={tokens} />;
        case 'heatmap':
            return <HeatmapBody data={data} accent={def.accent} />;
        case 'gauge':
            return <GaugeBody data={data} tokens={tokens} />;
        case 'alerts':
            return <AlertsBody data={data} />;
        case 'trend':
            return <TrendBody data={data} tokens={tokens} />;
        default:
            return null;
    }
}

/**
 * Source indicator. The label tracks the tile's true data state: `Live` once the
 * loader resolves, `Loading` while it's in flight, and `Sample` only once the
 * loader has finished without live data (genuine fallback) — or immediately for
 * tiles with no live loader at all. So "Sample" never shows on a tile that is
 * still resolving real data.
 */
function SourceTag({ isLive, loading, accent }: { isLive: boolean; loading: boolean; accent: VizAccentTokens }): ReactElement {
    const label = isLive ? 'Live' : loading ? 'Loading' : 'Sample';
    return (
        <Group gap={5} mt="sm" wrap="nowrap" align="center" justify="flex-end">
            {loading ? (
                <Loader size={9} color={accent.solid} />
            ) : (
                <Box style={{ width: 6, height: 6, borderRadius: '50%', background: isLive ? 'var(--bm-color-success)' : 'var(--bm-color-text-dimmed)', flexShrink: 0 }} />
            )}
            <Text c="dimmed" tt="uppercase" fw={700} style={{ fontSize: '8px', letterSpacing: '0.1em' }}>
                {label}
            </Text>
        </Group>
    );
}

function DeltaChip({ delta }: { delta: SignalDelta }): ReactElement {
    const color = delta.tone === 'good' ? 'var(--bm-color-success)' : delta.tone === 'bad' ? 'var(--bm-color-danger)' : 'var(--bm-color-text-dimmed)';
    const Icon = delta.direction === 'up' ? TrendingUp : delta.direction === 'down' ? TrendingDown : Minus;
    return (
        <Group gap={2} wrap="nowrap" align="center" style={{ color, flexShrink: 0 }}>
            <Icon size={13} />
            <Text ff="monospace" fw={700} style={{ fontSize: '11px' }}>
                {delta.label}
            </Text>
        </Group>
    );
}

function KpiBody({ data, tokens }: { data: SignalData; tokens: VizAccentTokens }): ReactElement {
    return (
        <Box>
            {data.spark !== undefined && <Sparkline values={data.spark} color={tokens.solid} />}
            <Group align="baseline" gap="xs" wrap="nowrap" mt={data.spark !== undefined ? 8 : 0}>
                <Text fw={700} ff="monospace" style={{ fontSize: '26px', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
                    {data.headline ?? '—'}
                </Text>
                {data.delta !== undefined && <DeltaChip delta={data.delta} />}
            </Group>
            {data.caption !== undefined && (
                <Text size="xs" c="dimmed" mt={3} style={{ fontSize: '11px' }}>
                    {data.caption}
                </Text>
            )}
        </Box>
    );
}

function segmentOpacity(rank: number): number {
    return Math.max(0.3, 1 - rank * 0.16);
}

function DonutBody({ data, tokens }: { data: SignalData; tokens: VizAccentTokens }): ReactElement {
    const series = (data.series ?? []).slice(0, 5);
    const total = series.reduce((sum, d) => sum + d.value, 0) || 1;
    const r = 26;
    const circumference = 2 * Math.PI * r;
    let offset = 0;
    return (
        <Group gap="md" wrap="nowrap" align="center">
            <svg width={66} height={66} viewBox="0 0 66 66" style={{ flexShrink: 0 }}>
                <g transform="rotate(-90 33 33)">
                    {series.map((d, i) => {
                        const len = (d.value / total) * circumference;
                        const dash = `${len.toFixed(2)} ${(circumference - len).toFixed(2)}`;
                        const node = (
                            <circle
                                key={`${i}-${d.label}`}
                                cx={33}
                                cy={33}
                                r={r}
                                fill="none"
                                stroke={tokens.solid}
                                strokeOpacity={segmentOpacity(i)}
                                strokeWidth={10}
                                strokeDasharray={dash}
                                strokeDashoffset={-offset}
                            />
                        );
                        offset += len;
                        return node;
                    })}
                </g>
            </svg>
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                {series.map((d, i) => (
                    <Group key={`${i}-${d.label}`} gap={6} wrap="nowrap" justify="space-between">
                        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                            <Box style={{ width: 8, height: 8, borderRadius: 2, background: tokens.solid, opacity: segmentOpacity(i), flexShrink: 0 }} />
                            <Text size="xs" truncate style={{ minWidth: 0 }}>
                                {d.label}
                            </Text>
                        </Group>
                        <Text size="xs" ff="monospace" c="dimmed" style={{ flexShrink: 0 }}>
                            {formatPercent(d.value / total)}
                        </Text>
                    </Group>
                ))}
            </Stack>
        </Group>
    );
}

function HeatmapBody({ data, accent }: { data: SignalData; accent: SignalTileDef['accent'] }): ReactElement | null {
    const heat = data.heat;
    if (heat === undefined) return null;
    const peak = heat.rows.reduce((max, row) => Math.max(max, ...row.values), 0) || 1;
    return (
        <Box style={{ overflowX: 'auto' }}>
            <Box style={{ display: 'grid', gridTemplateColumns: `minmax(56px, 1fr) repeat(${heat.columns.length}, minmax(18px, 1fr))`, gap: 2, alignItems: 'center' }}>
                <Box />
                {heat.columns.map((col) => (
                    <Text key={col} c="dimmed" ta="center" truncate style={{ fontSize: '8px' }}>
                        {col}
                    </Text>
                ))}
                {heat.rows.map((row) => (
                    <Fragment key={row.label}>
                        <Text fw={600} truncate style={{ fontSize: '10px' }}>
                            {row.label}
                        </Text>
                        {row.values.map((value, ci) => (
                            <Box
                                key={`${row.label}-${ci}`}
                                title={`${row.label} · ${heat.columns[ci] ?? ''}: ${formatSignalValue(heat.format, value)}`}
                                style={{ height: 20, borderRadius: 2, background: accentRgba(accent, 0.1 + 0.85 * (value / peak)) }}
                            />
                        ))}
                    </Fragment>
                ))}
            </Box>
        </Box>
    );
}

function GaugeBody({ data, tokens }: { data: SignalData; tokens: VizAccentTokens }): ReactElement | null {
    const gauge = data.gauge;
    if (gauge === undefined) return null;
    const fraction = Math.max(0, Math.min(1, gauge.fraction));
    const gaugeRadius = 34;
    const arcLength = Math.PI * gaugeRadius;
    const path = `M 8 46 A ${gaugeRadius} ${gaugeRadius} 0 0 1 76 46`;
    return (
        <Group gap="md" wrap="nowrap" align="center">
            <svg width={84} height={52} viewBox="0 0 84 52" style={{ flexShrink: 0 }}>
                <path d={path} fill="none" stroke="var(--bm-color-surface)" strokeWidth={8} strokeLinecap="round" />
                <path d={path} fill="none" stroke={tokens.solid} strokeWidth={8} strokeLinecap="round" strokeDasharray={`${(fraction * arcLength).toFixed(2)} ${arcLength.toFixed(2)}`} />
            </svg>
            <Box style={{ minWidth: 0 }}>
                <Text fw={700} ff="monospace" style={{ fontSize: '22px', lineHeight: 1, letterSpacing: '-0.02em' }}>
                    {gauge.valueText}
                </Text>
                {gauge.caption !== undefined && (
                    <Text size="xs" c="dimmed" mt={3} lineClamp={2} style={{ fontSize: '11px' }}>
                        {gauge.caption}
                    </Text>
                )}
            </Box>
        </Group>
    );
}

function AlertsBody({ data }: { data: SignalData }): ReactElement {
    const items = (data.alerts ?? []).slice(0, 4);
    return (
        <Stack gap={7}>
            {data.alertCount !== undefined && (
                <Text fw={700} ff="monospace" style={{ fontSize: '22px', lineHeight: 1, letterSpacing: '-0.02em' }}>
                    {formatCount(data.alertCount)}{' '}
                    <Text span c="dimmed" fw={500} style={{ fontSize: '11px' }}>
                        open
                    </Text>
                </Text>
            )}
            {items.map((alert, i) => (
                <Group key={`${alert.label}-${i}`} gap={8} wrap="nowrap" align="flex-start">
                    <Box style={{ width: 7, height: 7, borderRadius: '50%', background: SEVERITY_COLORS[alert.severity], flexShrink: 0, marginTop: 4 }} />
                    <Box style={{ flex: 1, minWidth: 0 }}>
                        <Group justify="space-between" gap="sm" wrap="nowrap">
                            <Text size="xs" fw={600} truncate style={{ minWidth: 0 }}>
                                {alert.label}
                            </Text>
                            {alert.meta !== undefined && (
                                <Text ff="monospace" c="dimmed" style={{ flexShrink: 0, fontSize: '10px' }}>
                                    {alert.meta}
                                </Text>
                            )}
                        </Group>
                        {alert.detail !== undefined && (
                            <Text c="dimmed" truncate style={{ fontSize: '10px' }}>
                                {alert.detail}
                            </Text>
                        )}
                    </Box>
                </Group>
            ))}
        </Stack>
    );
}

function TrendBody({ data, tokens }: { data: SignalData; tokens: VizAccentTokens }): ReactElement {
    const series = data.series ?? [];
    const values = series.map((d) => d.value);
    return (
        <Box>
            <Sparkline values={values} color={tokens.solid} height={58} />
            <Group justify="space-between" mt={4} wrap="nowrap">
                <Text c="dimmed" style={{ fontSize: '9px' }}>
                    {series[0]?.label ?? ''}
                </Text>
                <Text c="dimmed" style={{ fontSize: '9px' }}>
                    {series[series.length - 1]?.label ?? ''}
                </Text>
            </Group>
        </Box>
    );
}
